import React from "react";
import { Composition, Folder } from "remotion";
import {
  ScenarioExplainerClip,
  ScenarioExplainerThumbnail,
  calculateScenarioExplainerMetadata,
  calculateScenarioExplainerThumbnailMetadata,
} from "./social/ScenarioExplainerClip";
import { aaplLongCallClipInputProps } from "./social/fixtures/aaplLongCallScenario";
import {
  ClipV2,
  ClipV2Thumbnail,
  calculateClipV2Metadata,
  calculateClipV2ThumbnailMetadata,
} from "./social/clipV2/ClipV2";
import { CLIP_V2_FIXTURES, clipV2DefaultInputProps } from "./social/clipV2/fixtures/aaplClipV2Fixtures";

/**
 * Root registers all Remotion compositions.
 *
 * Launch scope: data-driven social clips only.
 *   - scenario-explainer-{9x16,1x1} (+ -thumbnail)
 *   - clip-v2-{9x16,1x1} (+ -thumbnail), plus Studio-only fixture copies
 *
 * The Brand Video Engine and the v0 scenario template pipeline are archived in
 * tag `archive/pre-launch-cleanup-2026-10` / branch `archive/brand-video-engine`.
 */

const FPS = 30;

export const Root = () => (
  <>
    <SocialClipCompositions />
    <ClipV2Compositions />
  </>
);

// Data-driven social clips (no AI-written scene code). Same components for both
// aspects; layout constants switch on width/height. Duration comes from
// calculateMetadata (beats / voiceover, clamped by renderLimits).
const SOCIAL_CLIP_ASPECTS = [
  { id: "scenario-explainer-9x16", thumbId: "scenario-explainer-9x16-thumbnail", width: 1080, height: 1920 },
  { id: "scenario-explainer-1x1", thumbId: "scenario-explainer-1x1-thumbnail", width: 1080, height: 1080 },
];

function SocialClipCompositions() {
  return (
    <>
      {SOCIAL_CLIP_ASPECTS.map((aspect) => (
        <React.Fragment key={aspect.id}>
          <Composition
            id={aspect.id}
            component={ScenarioExplainerClip}
            calculateMetadata={calculateScenarioExplainerMetadata}
            durationInFrames={900}
            fps={FPS}
            width={aspect.width}
            height={aspect.height}
            defaultProps={aaplLongCallClipInputProps}
          />
          <Composition
            id={aspect.thumbId}
            component={ScenarioExplainerThumbnail}
            calculateMetadata={calculateScenarioExplainerThumbnailMetadata}
            durationInFrames={1}
            fps={FPS}
            width={aspect.width}
            height={aspect.height}
            defaultProps={aaplLongCallClipInputProps}
          />
        </React.Fragment>
      ))}
    </>
  );
}

// clip-v2: stop-the-scroll social clips. One id per aspect (the API picks the
// format via inputProps.clip.format); Studio-only fixture copies per format live
// in the "clip-v2-fixtures" folder.
const CLIP_V2_ASPECTS = [
  { id: "clip-v2-9x16", thumbId: "clip-v2-9x16-thumbnail", aspectKey: "9x16", width: 1080, height: 1920 },
  { id: "clip-v2-1x1", thumbId: "clip-v2-1x1-thumbnail", aspectKey: "1x1", width: 1080, height: 1080 },
];

function ClipV2Pair({ id, thumbId, width, height, defaultProps }) {
  return (
    <>
      <Composition
        id={id}
        component={ClipV2}
        calculateMetadata={calculateClipV2Metadata}
        durationInFrames={900}
        fps={FPS}
        width={width}
        height={height}
        defaultProps={defaultProps}
      />
      <Composition
        id={thumbId}
        component={ClipV2Thumbnail}
        calculateMetadata={calculateClipV2ThumbnailMetadata}
        durationInFrames={1}
        fps={FPS}
        width={width}
        height={height}
        defaultProps={defaultProps}
      />
    </>
  );
}

function ClipV2Compositions() {
  return (
    <>
      {CLIP_V2_ASPECTS.map((aspect) => (
        <ClipV2Pair key={aspect.id} {...aspect} defaultProps={clipV2DefaultInputProps} />
      ))}
      <Folder name="clip-v2-fixtures">
        {Object.entries(CLIP_V2_FIXTURES).flatMap(([format, clip]) =>
          CLIP_V2_ASPECTS.map((aspect) => (
            <ClipV2Pair
              key={`${format}-${aspect.aspectKey}`}
              id={`clip-v2-${format}-${aspect.aspectKey}`}
              thumbId={`clip-v2-${format}-${aspect.aspectKey}-thumbnail`}
              width={aspect.width}
              height={aspect.height}
              defaultProps={{ clip }}
            />
          ))
        )}
      </Folder>
    </>
  );
}
